import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2" }
const _hoisted_2 = { class: "flex flex-col gap-6 p-4 sm:p-6" }
const _hoisted_3 = { class: "grid grid-cols-2 gap-4 lg:grid-cols-6" }
const _hoisted_4 = {
  key: 0,
  class: "flex flex-wrap items-center gap-1 px-4 pb-3 text-xs text-gray-500"
}
const _hoisted_5 = {
  key: 0,
  "data-test": "mixed-currency-notice",
  class: "-mt-3 text-xs text-gray-500"
}

import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import ProductionDayCalendar from '@/components/activity/ProductionDayCalendar.vue'
import StatTile from '@/components/customers/record/StatTile.vue'
import FranchiseLeaderboard from '@/components/franchise/FranchiseLeaderboard.vue'
import FranchiseMerchantSwitcher from '@/components/franchise/FranchiseMerchantSwitcher.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import ReportChart from '@/components/reports/ReportChart.vue'
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter.vue'
import ReportDelta from '@/components/reports/ReportDelta.vue'
import SecondaryNav from '@/components/SecondaryNav.vue'
import SecondaryTop from '@/components/SecondaryTop.vue'
import { useFranchiseDashboard } from '@/composables/useFranchiseDashboard'
import { merchantLocation, merchantName } from '@/helpers/franchiseMerchant'
import { DATE_RANGE_OPTIONS } from '@/helpers/reportDateRanges'
import {
  bucketFranchiseRows,
  groupByForRange,
  inclusiveDays,
  salesByLocationChartData,
  salesOverTimeChartData,
  tileFigures
} from '@/services/franchiseSales.service'


export default {
  __name: 'FranchiseHome',
  setup(__props) {

const store = useStore()
const router = useRouter()

const {
  range,
  setRange,
  activeMerchantId,
  loading,
  summary,
  salesRows,
  seriesOptions,
  leaderboard,
  days,
  calendarCurrent,
  load,
  loadCalendar
} = useFranchiseDashboard()

const hideNumbers = computed(() => !!store.state.hideNumbers)
const activeMerchant = computed(() => store.getters.activeFranchiseMerchant)
const franchiseName = computed(() => store.state.franchise?.attributes?.name || 'Franchise')

const title = computed(() => (activeMerchant.value ? merchantName(activeMerchant.value) : franchiseName.value))

const description = computed(() => {
  if (activeMerchant.value) return merchantLocation(activeMerchant.value) || franchiseName.value
  const count = store.getters.franchiseMerchants.length
  return `Across ${count} ${count === 1 ? 'location' : 'locations'}`
})

const tiles = computed(() => tileFigures(summary.value ?? {}))

const previousCaption = computed(() => {
  const previous = summary.value?.previous_range
  if (!previous) return 'vs previous period'
  const count = inclusiveDays(previous.beginning_date, previous.ending_date)
  return count === 1 ? 'vs previous day' : `vs previous ${count} days`
})

const bucketed = computed(() => {
  const { beginning_date, ending_date } = range.apiDateRange()
  return bucketFranchiseRows(salesRows.value, groupByForRange(beginning_date, ending_date), {
    from: beginning_date,
    to: ending_date
  })
})
const overTime = computed(() => salesOverTimeChartData(bucketed.value))
const byLocation = computed(() =>
  salesByLocationChartData(bucketed.value, seriesOptions.value, {
    seriesKind: activeMerchantId.value ? 'store' : 'merchant'
  })
)

const selectMerchant = id => store.dispatch('setActiveFranchiseMerchant', id)
const openDay = date => router.push(`/dashboard/days/${date}`)

onMounted(load)

return (_ctx, _cache) => {
  return (_openBlock(), _createBlock(SecondaryNav, { "full-width": "" }, {
    top: _withCtx(() => [
      _createVNode(SecondaryTop, {
        text: title.value,
        description: description.value
      }, {
        right: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(ReportDateRangeFilter, {
              options: _unref(DATE_RANGE_OPTIONS),
              selected: _unref(range).selectedDateRange.value,
              "selected-label": _unref(range).selectedDateRangeLabel.value,
              "custom-range": _unref(range).customDateRange.value,
              onChange: _unref(setRange)
            }, null, 8, ["options", "selected", "selected-label", "custom-range", "onChange"]),
            _createVNode(FranchiseMerchantSwitcher)
          ])
        ]),
        _: 1
      }, 8, ["text", "description"])
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tiles.value, (tile) => {
            return (_openBlock(), _createElementBlock("div", {
              key: tile.key,
              "data-test": "franchise-tile",
              class: "rounded-lg bg-white shadow ring-1 ring-black ring-opacity-5"
            }, [
              _createVNode(StatTile, {
                label: tile.label,
                value: String(tile.value),
                loading: _unref(loading).summary
              }, null, 8, ["label", "value", "loading"]),
              (!_unref(loading).summary)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(ReportDelta, {
                      delta: tile.delta
                    }, null, 8, ["delta"]),
                    _createElementVNode("span", null, _toDisplayString(previousCaption.value), 1)
                  ]))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]),
        (_unref(summary)?.mixed_currency)
          ? (_openBlock(), _createElementBlock("p", _hoisted_5, " Locations use more than one currency. Totals are summed as reported. "))
          : _createCommentVNode("", true),
        _createVNode(ReportChart, {
          title: "Sales over time",
          "chart-data": overTime.value,
          "chart-type": "line",
          loading: _unref(loading).sales,
          "currency-format": true,
          gridlines: true,
          "empty-state-title": "No sales in this period"
        }, null, 8, ["chart-data", "loading"]),
        _createVNode(ReportChart, {
          title: _unref(activeMerchantId) ? 'Sales by store' : 'Sales by location',
          "chart-data": byLocation.value.chartData,
          "chart-type": "stackedBar",
          loading: _unref(loading).sales,
          "has-supporting-table": true,
          "table-data": byLocation.value.tableData,
          "label-column-title": "Location",
          "currency-format": true,
          gridlines: true,
          "empty-state-title": "No sales in this period"
        }, null, 8, ["title", "chart-data", "loading", "table-data"]),
        (!_unref(activeMerchantId))
          ? (_openBlock(), _createBlock(NeueLoading, {
              key: 1,
              loading: _unref(loading).leaderboard
            }, {
              default: _withCtx(() => [
                _createVNode(FranchiseLeaderboard, {
                  rows: _unref(leaderboard),
                  onSelect: selectMerchant
                }, null, 8, ["rows"])
              ]),
              _: 1
            }, 8, ["loading"]))
          : _createCommentVNode("", true),
        _createVNode(NeueLoading, {
          loading: _unref(loading).calendar
        }, {
          default: _withCtx(() => [
            _createVNode(ProductionDayCalendar, {
              days: _unref(days),
              current: _unref(calendarCurrent),
              "hide-numbers": hideNumbers.value,
              "show-uber": false,
              "show-subscribers": false,
              "onUpdate:current": _cache[0] || (_cache[0] = $event => (calendarCurrent.value = $event)),
              "onUpdate:range": _unref(loadCalendar),
              onSelect: openDay
            }, null, 8, ["days", "current", "hide-numbers", "onUpdate:range"])
          ]),
          _: 1
        }, 8, ["loading"])
      ])
    ]),
    _: 1
  }))
}
}

}